import {Component, OnInit, OnDestroy, AfterViewInit} from '@angular/core';
import { NameAndAddressService } from 'src/app/services/name-and-address/name-and-address.service';
import { ActivatedRoute } from '@angular/router';
import { UntypedFormGroup, UntypedFormBuilder } from '@angular/forms';
import { combineLatest, BehaviorSubject, Observable } from 'rxjs';
import { map, takeWhile } from 'rxjs/operators';
import { Location } from '@angular/common';
import { NameAndAddressRecordType } from 'src/app/shared/common-components/AppConstants';
import {Address, NameAndAddress, ShipmentPrefAddress} from 'src/app/shared/common-components/common-structure';
import { BsModalService, BsModalRef } from 'ngx-bootstrap/modal';
import { ShipmentPreferredAddressesComponent } from './shipment-preferred-addresses/shipment-preferred-addresses.component';
import { LoadingService } from 'src/app/services/loading/loading.service';
import { AlertService } from 'src/app/services/alert/alert.service';

@Component({
    selector: 'app-shipment-addresses',
    templateUrl: './shipment-addresses.component.html',
    styleUrls: ['./shipment-addresses.component.css'],
    standalone: false
})
export class ShipmentAddressesComponent implements OnInit, OnDestroy, AfterViewInit {

  shipmentAddressForm: UntypedFormGroup;
  bsModalRef: BsModalRef;
  nameAndAddressRecordType = NameAndAddressRecordType;
  saveAddresses$: Observable<any>;
  originSelectedAddress = new BehaviorSubject<NameAndAddress>(null);
  shipperSelectedAddress = new BehaviorSubject<NameAndAddress>(null);
  billToSelectedAddress = new BehaviorSubject<NameAndAddress>(null);
  destinationSelectedAddress = new BehaviorSubject<NameAndAddress>(null);
  consigneeSelectedAddress = new BehaviorSubject<NameAndAddress>(null);
  isFormInvalidValue = [];

  private observer$ = new BehaviorSubject(true);

  user$ = this.nameAndAddressService.user$;
  originAddress$ = combineLatest([this.nameAndAddressService.allShipmentAddresses$, this.originSelectedAddress])
    .pipe(map(([shipmentAddresses, selectedAddress]) => selectedAddress
      ? selectedAddress : shipmentAddresses.find(address => address.recordType === NameAndAddressRecordType.ORIGIN)));

  shipperAddress$ = combineLatest([this.nameAndAddressService.allShipmentAddresses$, this.shipperSelectedAddress])
    .pipe(map(([shipmentAddresses, selectedAddress]) => selectedAddress
      ? selectedAddress : shipmentAddresses.find(address => address.recordType === NameAndAddressRecordType.SHIPPER)));

  billToAddress$ = combineLatest([this.nameAndAddressService.allShipmentAddresses$, this.billToSelectedAddress])
    .pipe(map(([shipmentAddresses, selectedAddress]) => selectedAddress
      ? selectedAddress : shipmentAddresses.find(address => address.recordType === NameAndAddressRecordType.BILL_TO)));

  destinationAddress$ = combineLatest([this.nameAndAddressService.allShipmentAddresses$, this.destinationSelectedAddress])
    .pipe(map(([shipmentAddresses, selectedAddress]) => selectedAddress
      ? selectedAddress : shipmentAddresses.find(address => address.recordType === NameAndAddressRecordType.DESTINATION)));

  consigneeAddress$ = combineLatest([this.nameAndAddressService.allShipmentAddresses$, this.consigneeSelectedAddress])
    .pipe(map(([shipmentAddresses, selectedAddress]) => selectedAddress
      ? selectedAddress : shipmentAddresses.find(address => address.recordType === NameAndAddressRecordType.CONSIGNEE)));

  issuingAddress$ = this.nameAndAddressService.allShipmentAddresses$.pipe(map(shipmentAddresses =>
    shipmentAddresses.find(address => address.recordType === NameAndAddressRecordType.ISSUE_OFFICE)));


  vm$ = combineLatest([
    this.originAddress$, this.shipperAddress$,
    this.billToAddress$, this.destinationAddress$,
    this.consigneeAddress$, this.issuingAddress$, this.user$,
    this.nameAndAddressService.allStates$, this.nameAndAddressService.allCountriesList$])
    .pipe(map(([originAddress, shipperAddress, billToAddress, destinationAddress,
      consigneeAddress, issuingAddress, user, states, countries]) =>
      ({
        originAddress, shipperAddress, billToAddress, destinationAddress,
        consigneeAddress, issuingAddress, user, states, countries
      })));

  constructor(private nameAndAddressService: NameAndAddressService, private formBuilder: UntypedFormBuilder,
    private location: Location, private modalService: BsModalService, private loader: LoadingService,
    private route: ActivatedRoute, private alertService: AlertService) {
    this.shipmentAddressForm = this.formBuilder.group({
      origin: [],
      shipper: [],
      billTo: [],
      destination: [],
      consignee: [],
      issuingOffice: []
    });
  }

  ngOnInit() {
    this.route.queryParams.pipe(takeWhile(() => this.observer$.value))
    .subscribe(queryParams => this.nameAndAddressService.updateShipmentId(queryParams.shipmentid));
  }

  ngAfterViewInit() {
    window.scroll(0, 0);
  }

  copyShipperAddressToBillToAddress(shipperAddress: NameAndAddress) {
    const billToAddress = this.shipmentAddressForm.value.shipper ? { ...shipperAddress, ...this.shipmentAddressForm.value.shipper }
      : { ...shipperAddress };
    billToAddress.nameAddressId = this.shipmentAddressForm.value.billTo.nameAddressId ? this.shipmentAddressForm.value.billTo.nameAddressId : null;
    billToAddress.recordType = NameAndAddressRecordType.BILL_TO;
    billToAddress['displayAddressType'] = 'Bill To Address';
    this.billToSelectedAddress.next(billToAddress);
  }

  copyDestinationAddressToConsigneeAddress(destinationAddress: NameAndAddress) {
    const consigneeAddress: NameAndAddress = this.shipmentAddressForm.value.destination ?
      { ...destinationAddress, ...this.shipmentAddressForm.value.destination } : { ...destinationAddress };
    consigneeAddress.nameAddressId = this.shipmentAddressForm.value.consignee.nameAddressId ? this.shipmentAddressForm.value.consignee.nameAddressId : null;
    consigneeAddress.recordType = NameAndAddressRecordType.CONSIGNEE;
    consigneeAddress['displayAddressType'] = 'Consignee Address';
    this.consigneeSelectedAddress.next(consigneeAddress);
  }

  isSubmitDisable(): boolean {
    this.isFormInvalidValue = [];
      Object.entries(this.shipmentAddressForm.controls)
        .filter(k => k[0] !== 'shipper' && k[0] !== 'consignee')
        .forEach(v => {
        if (v[1] && v[1].dirty && v[1].invalid && !this.isFormInvalidValue.includes(v[1].value?.recordType)) {
          this.isFormInvalidValue.push(v[1].value?.recordType);
        }
      });
    return Object.entries(this.shipmentAddressForm.controls).filter(k => k[0] !== 'shipper' && k[0] !== 'consignee').every(v => v[1] && !v[1].dirty) ||
      Object.entries(this.shipmentAddressForm.controls).filter(k => k[0] !== 'shipper' && k[0] !== 'consignee').some(v => v[1] && v[1].dirty && v[1].invalid);
    /*let disableStatus = true;
    if ((Object.entries(this.shipmentAddressForm.controls)[0][1].status === 'VALID') &&
        (Object.entries(this.shipmentAddressForm.controls)[2][1].status === 'VALID') &&
        (Object.entries(this.shipmentAddressForm.controls)[3][1].status === 'VALID')) {
          disableStatus = false;
        }
    return disableStatus;*/
  }

  onSubmit() {
    const saveAddresses = Object.entries(this.shipmentAddressForm.value).filter(v => v[1]).map(v => v[1] as NameAndAddress);
    saveAddresses.forEach(shipmentAddress => {
      shipmentAddress.shipmentsId = this.route.snapshot.queryParams.shipmentid;
      if (shipmentAddress.recordType === 'Issue Office') {
        shipmentAddress.countryId = 326;
        shipmentAddress.stateId = 1;
      }
    });
    this.loader.show();
    this.nameAndAddressService.saveAddresses(saveAddresses).pipe(takeWhile(() => this.observer$.value)).subscribe(() => {
      this.loader.hide();
      this.alertService.success('Successfully saved the shipment Address');
      this.location.back();
    },
      () => {
        this.alertService.error('Error saving the shipment Address');
        this.loader.hide();
      });
  }


  openPreferredAddress(currentUser, selectedNameAndAddress: NameAndAddress) {
    const initialState = {
      currentUser: currentUser,
      selectedNameAndAddress: selectedNameAndAddress,
      callBackFn: (selectedAddress: ShipmentPrefAddress) => {
        const nameAndAddress = { ...new NameAndAddress(), ...selectedAddress, ... { shipmentsId: this.route.snapshot.queryParams.shipmentid },
        ... { nameAddressId: selectedNameAndAddress.nameAddressId } } as NameAndAddress;
        this.updatedAddressWithPreferredAddress(nameAndAddress);
      }
    };
    this.bsModalRef = this.modalService.show(ShipmentPreferredAddressesComponent, { initialState, class: 'modal-xl' });
    this.bsModalRef.content.closeBtnName = 'Close';
  }

  goBack() {
    this.location.back();
  }

  updatedAddressWithPreferredAddress(nameAndAddress: NameAndAddress) {
    switch (nameAndAddress.recordType) {
      case NameAndAddressRecordType.ORIGIN: this.originSelectedAddress.next(nameAndAddress); break;
      case NameAndAddressRecordType.SHIPPER: this.shipperSelectedAddress.next(nameAndAddress); break;
      case NameAndAddressRecordType.BILL_TO: this.billToSelectedAddress.next(nameAndAddress); break;
      case NameAndAddressRecordType.DESTINATION: this.destinationSelectedAddress.next(nameAndAddress); break;
      case NameAndAddressRecordType.CONSIGNEE: this.consigneeSelectedAddress.next(nameAndAddress); break;
      default: break;
    }
  }

  getAddress(address: Address): string {
    return [address.addressLine1, address.addressLine2, address.city, address.stateId, address.zipCode]
        .filter((value) => !!value).join(', ');
  }

  ngOnDestroy(): void {
    this.observer$.next(false);
  }
}
